import { existsSync, readFileSync, readdirSync, statSync } from 'fs';
import { join, basename } from 'path';

export interface ResolvedValue<T> {
  value: T | null;
  source: string | null;
}

export interface AppSettingsLoad {
  base: Record<string, unknown> | null;
  override: Record<string, unknown> | null;
  baseFile: string | null;
  overrideFile: string | null;
  merged: Record<string, unknown>;
}

export interface FileMeta {
  path: string;
  mtime: string;
  size: number;
}

/**
 * Find all appsettings*.json files in an API folder.
 */
export function findAppSettings(apiFolder: string): string[] {
  if (!existsSync(apiFolder)) {
    return [];
  }
  const files = readdirSync(apiFolder);
  return files
    .filter((f) => f.startsWith('appsettings') && f.endsWith('.json'))
    .map((f) => join(apiFolder, f));
}

/**
 * Load base + mode-specific appsettings and deep-merge them.
 * The mode-specific file overrides the base (ASP.NET Core convention).
 */
export function loadAppSettings(apiDir: string, mode: string | null): AppSettingsLoad {
  const baseFile = join(apiDir, 'appsettings.json');
  const overrideFile = mode ? join(apiDir, `appsettings.${mode}.json`) : null;

  const base = readJsonSafe(baseFile);
  const override = overrideFile ? readJsonSafe(overrideFile) : null;

  return {
    base,
    override,
    baseFile: existsSync(baseFile) ? baseFile : null,
    overrideFile: overrideFile && existsSync(overrideFile) ? overrideFile : null,
    merged: deepMerge(base ?? {}, override ?? {}),
  };
}

function readJsonSafe(filePath: string): Record<string, unknown> | null {
  try {
    if (!existsSync(filePath)) return null;
    return JSON.parse(readFileSync(filePath, 'utf-8'));
  } catch {
    return null;
  }
}

/**
 * Deep merge two plain objects. Arrays and primitives in override replace base.
 */
export function deepMerge(base: unknown, override: unknown): Record<string, unknown> {
  if (override === null || override === undefined) {
    return (base as Record<string, unknown>) ?? {};
  }
  if (base === null || base === undefined || typeof base !== 'object' || Array.isArray(base)) {
    return (override as Record<string, unknown>) ?? {};
  }
  if (typeof override !== 'object' || Array.isArray(override)) {
    return base as Record<string, unknown>;
  }
  const result: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const key of Object.keys(override as Record<string, unknown>)) {
    const baseVal = (base as Record<string, unknown>)[key];
    const overrideVal = (override as Record<string, unknown>)[key];
    if (
      baseVal !== null &&
      typeof baseVal === 'object' &&
      !Array.isArray(baseVal) &&
      overrideVal !== null &&
      typeof overrideVal === 'object' &&
      !Array.isArray(overrideVal)
    ) {
      result[key] = deepMerge(baseVal, overrideVal);
    } else {
      result[key] = overrideVal;
    }
  }
  return result;
}

/**
 * Walk a JSON object using a dotted path (e.g. "Security.InitialAdmin.Email").
 */
function getByPath(obj: Record<string, unknown> | null, path: string[]): unknown {
  if (!obj) return undefined;
  let current: unknown = obj;
  for (const key of path) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/**
 * Resolve which file (override → base) defined a given JSON path.
 */
function resolveSource(
  load: AppSettingsLoad,
  path: string[]
): { value: unknown; source: string | null } {
  const overrideVal = getByPath(load.override, path);
  if (overrideVal !== undefined) {
    return { value: overrideVal, source: load.overrideFile ? basename(load.overrideFile) : null };
  }
  const baseVal = getByPath(load.base, path);
  if (baseVal !== undefined) {
    return { value: baseVal, source: load.baseFile ? basename(load.baseFile) : null };
  }
  return { value: undefined, source: null };
}

/**
 * Extract the DefaultConnection string from an appsettings JSON file.
 * Returns null if not found or file is unparseable. (Legacy single-file API.)
 */
export function extractConnectionString(filePath: string): string | null {
  try {
    const content = JSON.parse(readFileSync(filePath, 'utf-8'));
    return content?.ConnectionStrings?.DefaultConnection || null;
  } catch {
    return null;
  }
}

/**
 * Extract the DefaultConnection string from a merged appsettings load,
 * preferring the override file. Returns the value and the source filename.
 */
export function extractMergedConnectionString(load: AppSettingsLoad): ResolvedValue<string> {
  const { value, source } = resolveSource(load, ['ConnectionStrings', 'DefaultConnection']);
  return { value: typeof value === 'string' ? value : null, source };
}

/**
 * Parse a SQL Server connection string into its components.
 */
export function parseConnectionString(connStr: string): {
  server: string;
  database: string;
  useWindowsAuth: boolean;
} {
  const parts = connStr.split(';').reduce(
    (acc, part) => {
      const eqIdx = part.indexOf('=');
      if (eqIdx < 0) return acc;
      const key = part.slice(0, eqIdx).trim();
      const value = part.slice(eqIdx + 1).trim();
      if (key && value) acc[key.toLowerCase()] = value;
      return acc;
    },
    {} as Record<string, string>
  );

  return {
    server: parts['server'] || parts['data source'] || 'localhost',
    database: parts['database'] || parts['initial catalog'] || 'SmartStack',
    useWindowsAuth:
      parts['integrated security']?.toLowerCase() === 'true' ||
      parts['trusted_connection']?.toLowerCase() === 'true',
  };
}

/**
 * Read Security.InitialAdmin credentials from an appsettings JSON file.
 * Returns null if the section is absent or if Email/Password are missing.
 * Supports SmartStack.app v3.46+. (Legacy single-file API.)
 */
export function extractInitialAdmin(filePath: string): { email: string; password: string } | null {
  try {
    const content = JSON.parse(readFileSync(filePath, 'utf-8'));
    const admin = content?.Security?.InitialAdmin;
    if (!admin) return null;
    const email = admin.Email || admin.email;
    const password = admin.Password || admin.password;
    if (!email || !password) return null;
    return { email, password };
  } catch {
    return null;
  }
}

/**
 * Read Security.InitialAdmin from a merged appsettings load.
 * Prefers the override file. Returns the credentials and the source filename.
 */
export function extractMergedInitialAdmin(
  load: AppSettingsLoad
): ResolvedValue<{ email: string; password: string }> {
  // Read from the DEEP-MERGED config, not the override's whole object. ASP.NET
  // layers appsettings.{Env}.json over appsettings.json key-by-key, so a partial
  // override must combine with the base at the LEAF: e.g. base supplies Email and
  // appsettings.Local.json supplies the real plaintext Password (base may carry a
  // hash/placeholder). The old resolveSource(['Security','InitialAdmin']) returned
  // the override's object wholesale → dropped base's Email and never surfaced the
  // Local password. load.merged is exactly what the running backend seeds.
  const adminVal = getByPath(load.merged, ['Security', 'InitialAdmin']);
  if (!adminVal || typeof adminVal !== 'object' || Array.isArray(adminVal)) {
    return { value: null, source: null };
  }
  const admin = adminVal as Record<string, unknown>;
  const email = (admin.Email || admin.email) as string | undefined;
  const password = (admin.Password || admin.password) as string | undefined;
  if (!email || !password) return { value: null, source: null };
  // Attribute the source to wherever the PASSWORD leaf was defined (override
  // wins) — that is the security-relevant value shown to the user.
  const source =
    resolveSource(load, ['Security', 'InitialAdmin', 'Password']).source ??
    resolveSource(load, ['Security', 'InitialAdmin', 'password']).source ??
    null;
  return { value: { email, password }, source };
}

/**
 * Read Cors.AllowedOrigins (or equivalent) from a merged appsettings load.
 */
export function extractMergedCorsOrigins(load: AppSettingsLoad): ResolvedValue<string[]> {
  const candidates: string[][] = [
    // Canonical SmartStack key (what `ss dev configure` writes + the app reads),
    // checked first so detect verifies exactly what was written; the rest are
    // legacy fallbacks so existing projects still resolve.
    ['SmartStack', 'CorsOrigins'],
    ['Cors', 'AllowedOrigins'],
    ['CorsOptions', 'AllowedOrigins'],
    ['AllowedOrigins'],
  ];
  for (const path of candidates) {
    const { value, source } = resolveSource(load, path);
    if (value !== undefined && value !== null) {
      const arr = Array.isArray(value) ? (value as string[]) : [String(value)];
      return { value: arr, source };
    }
  }
  return { value: [], source: null };
}

/**
 * Read Authentication.FrontendUrl from a merged appsettings load.
 */
export function extractMergedFrontendUrl(load: AppSettingsLoad): ResolvedValue<string> {
  // Canonical key the SmartStack app + `ss dev configure` use is
  // SmartStack.FrontendUrl; older apps carry Authentication.FrontendUrl. Read the
  // canonical one first (so detect verifies exactly what configure writes), then
  // fall back to the legacy key so existing projects still resolve.
  const candidates: string[][] = [
    ['SmartStack', 'FrontendUrl'],
    ['Authentication', 'FrontendUrl'],
  ];
  for (const path of candidates) {
    const { value, source } = resolveSource(load, path);
    if (typeof value === 'string') return { value, source };
  }
  return { value: null, source: null };
}

/**
 * Read the file log sink config (Logging:Sinks:File) from a merged appsettings
 * load. Returns { enabled, path } and the source filename.
 * `path` is the configured path template (e.g. "logs/smartstack-.log"); the
 * Serilog file sink resolves it against the process working directory (the API
 * dir when launched via `dotnet run`). Key + defaults match SmartStack.app
 * `UseSmartStackSerilog` / `ConfigureFileSink` (section "Logging:Sinks").
 */
export function extractMergedSerilogFile(
  load: AppSettingsLoad
): ResolvedValue<{ enabled: boolean; path: string }> {
  const { value, source } = resolveSource(load, ['Logging', 'Sinks', 'File']);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { value: null, source: null };
  }
  const file = value as Record<string, unknown>;
  const enabled = Boolean(file.Enabled ?? file.enabled);
  const rawPath = (file.Path ?? file.path) as string | undefined;
  const path = typeof rawPath === 'string' && rawPath.length > 0 ? rawPath : 'logs/smartstack-.log';
  return { value: { enabled, path }, source };
}

/**
 * Extract the environment name from an appsettings filename.
 */
export function getEnvFromFilename(filePath: string): string | null {
  const name = basename(filePath);
  const match = name.match(/^appsettings\.(.+)\.json$/i);
  if (!match) return null;
  return match[1];
}

/**
 * Compute mtime + size for a list of files. Used for cache coherence checks.
 */
export function computeFilesMeta(paths: string[]): FileMeta[] {
  return paths.map((p) => {
    try {
      const st = statSync(p);
      return { path: p, mtime: st.mtimeMs ? new Date(st.mtimeMs).toISOString() : '0', size: st.size };
    } catch {
      return { path: p, mtime: '0', size: 0 };
    }
  });
}

/**
 * Compare two FileMeta arrays. Returns the list of changed paths.
 */
export function diffFilesMeta(previous: FileMeta[], current: FileMeta[]): string[] {
  const changed: string[] = [];
  const prevMap = new Map(previous.map((m) => [m.path, m]));
  for (const c of current) {
    const p = prevMap.get(c.path);
    if (!p || p.mtime !== c.mtime || p.size !== c.size) {
      changed.push(c.path);
    }
  }
  const currentPaths = new Set(current.map((c) => c.path));
  for (const p of previous) {
    if (!currentPaths.has(p.path)) {
      changed.push(p.path);
    }
  }
  return changed;
}
